import Vue from 'vue';
import VueRouter from 'vue-router';
import GameModel from 'Models/GameModel';
import SeoModel from 'Models/SeoModel';
import googleTagAnalytics from 'Root/drivers/googleTagAnalytics';
import IntercomApiDriver from 'Root/drivers/IntercomApiDriver';
import criteoAnalytics from 'Root/drivers/criteoAnalytics';
import getBlue from 'Root/drivers/getBlue';
import ClientCatalogModel from 'Models/ClientCatalogModel';
import {
  executeAfterLoad,
  isUserHasRole,
  setNotFoundState,
  splitUrl,
} from 'Root/helpers/tools';

import { extractIdFromSlug } from 'Root/helpers/product';
import { saveAdvertisingParamsToSession } from 'Root/helpers/handleAdvertisingParams';
import searchStore from 'Root/store/modules/search';
import SignInModel from 'Models/SignInModel';
import UserModel from 'Models/UserModel';
import admin from './admin';
import games from './games';
import blog from './blog';
import booster from './booster';
import client from './client';

Vue.use(VueRouter);

/* async function checkIsAuthorized(to, from, next) {
  const { user } = store.state;
  if (user) {
    next();
  } else {
    next('/');
  }
} */

function updateCanonical(to) {
  if (typeof document !== 'undefined') {
    const canonical = document.querySelector('link[rel="canonical"]');
    canonical.href = `${window.location.protocol}//${window.location.host}${to.path}`;
  }
}

function getActiveGame(context, vm) {
  let gameId = null;

  const activeGameCookie = typeof window === 'undefined'
    ? context.cookies.activeGame
    : vm.$cookie.get('activeGame');

  if (activeGameCookie) {
    return +activeGameCookie;
  }

  // get game from url
  const [, gameNamespace = null] = splitUrl(context);

  if (gameNamespace) {
    const game = GameModel.LIST.find((g) => `${g.routeNamespace}-boost` === gameNamespace);
    if (game && game.active) {
      gameId = game.id;
    }
  }

  // look game in storage
  if (!gameId && typeof window !== 'undefined' && 'localStorage' in window) {
    gameId = +localStorage.getItem('activeGameId');
  }

  return gameId || GameModel.WOW.id; // fallback game
}

export function isNavigationAllowed(route, user) {
  let isAllowed = true;

  if (route?.meta?.roles && !isUserHasRole(user, route.meta.roles)) {
    isAllowed = false;
  }

  if (route?.meta?.allowedUserIds && route?.meta?.allowedUserIds.includes(user.id)) {
    isAllowed = true;
  }

  return isAllowed;
}

function getRedirectPath(route) {
  let redirectOptions;
  const {
    skycoach_token: token,
    email,
  } = route.query;

  if (token && email) {
    redirectOptions = {
      name: 'root',
      query: {
        email,
        skycoach_token: token,
        redirectPathAfterSignIn: route.fullPath,
      },
    };
  } else {
    redirectOptions = {
      name: 'root',
      query: {
        signInComponent: SignInModel.POPUP_VIEWS.SIGN_IN,
        redirectPathAfterSignIn: route.fullPath,
      },
    };
  }

  return redirectOptions;
}

export default function createRouter(store, context) {
  if (!store.state.activeGame) {
    const gameId = getActiveGame(context, store._vm);
    store.commit('mutateActiveGame', gameId);
  }

  const routes = [
    ...admin(store),
    ...booster(store),
    ...client(store),
    {
      path: '',
      component: () => import('../Showcase.vue'),
      children: [
        ...games(store),
        blog(store),
        {
          name: 'root',
          path: '/',
          component: () => import(/* webpackChunkName: 'main-page' */'../pages/mainPage/MainPage.vue'),
        },
        {
          name: 'sign-in',
          path: '/sign-in',
          redirect: {
            name: 'root',
            query: {
              signInComponent: SignInModel.POPUP_VIEWS.SIGN_IN,
            },
          },
        },
        {
          name: 'product-detail',
          path: '/products/:id',
          redirect: () => {
            // Lol has no products, so pass all its products routes to wow
            if (store.state.activeGame.id === GameModel.LOL.id) {
              store.commit('mutateActiveGame', GameModel.WOW.id);
            }
            return { name: 'game__product-detail', params: { namespace: GameModel.WOW.routeNamespace } };
          },
        },
        {
          name: 'order-success',
          path: '/order/:id',
          meta: {
            shouldUseBg: ClientCatalogModel.BG.DEFAULT,
          },
          redirect: (to) => ({
            name: 'client__order-progress',
            params: {
              id: to.params.id,
            },
          }),
        },
        {
          name: 'faq',
          path: '/faq',
          meta: {
            shouldUseBg: ClientCatalogModel.BG.NEUTRAL,
            isCenteredPage: true,
          },
          component: () => import('../pages/static/DocsFaq.vue'),
        },
        {
          name: 'agreement',
          path: '/agreement',
          meta: {
            shouldUseBg: ClientCatalogModel.BG.NEUTRAL,
            isCenteredPage: true,
          },
          component: () => import('../pages/static/DocsAgreement.vue'),
        },
        {
          name: 'agreement-coach',
          path: '/agreement/coach',
          meta: {
            shouldUseBg: ClientCatalogModel.BG.NEUTRAL,
            isCenteredPage: true,
          },
          component: () => import('../pages/static/DocsAgreementCoach.vue'),
        },
        {
          name: 'agreement-customer',
          path: '/agreement/customer',
          meta: {
            shouldUseBg: ClientCatalogModel.BG.NEUTRAL,
            isCenteredPage: true,
          },
          component: () => import('../pages/static/DocsAgreementClient.vue'),
        },
        {
          name: 'cookie',
          path: '/cookie',
          meta: {
            shouldUseBg: ClientCatalogModel.BG.NEUTRAL,
            isCenteredPage: true,
          },
          component: () => import('../pages/static/DocsCookie.vue'),
        },
        {
          name: 'privacy',
          path: '/privacy',
          meta: {
            shouldUseBg: ClientCatalogModel.BG.NEUTRAL,
            isCenteredPage: true,
          },
          component: () => import('../pages/static/DocsPrivacy.vue'),
        },
        {
          name: 'contacts',
          path: '/contacts',
          meta: {
            shouldUseBg: ClientCatalogModel.BG.CUSTOM,
            shouldUseBgDirectory: 'contacts',
            isCenteredPage: true,
          },
          component: () => import('../pages/static/ContactUs.vue'),
        },
        {
          name: 'sitemap',
          path: '/sitemaps',
          meta: {
            shouldUseBg: ClientCatalogModel.BG.NEUTRAL,
            isCenteredPage: true,
          },
          component: () => import('../pages/sitemap/Sitemap.vue'),
        },
        {
          path: '/search',
          component: () => import('../pages/game/GameCore.vue'),
          beforeEnter(to, from, next) {
            if (!store.hasModule('search')) {
              const options = {};
              if (store.state.search) {
                options.preserveState = true;
              }

              store.registerModule('search', searchStore, options);
            }

            next();
          },
          children: [
            {
              name: 'search',
              path: '/search/:query?',
              meta: {
                shouldUseBg: ClientCatalogModel.BG.NEUTRAL,
                isCenteredPage: true,
              },
              component: () => import('../pages/game/searchResult/SearchResult.vue'),
            },
          ],
        },
        {
          name: 'seller',
          path: '/seller/:id',
          meta: {},
          component: () => import('../pages/users/Users.vue'),
        },
        {
          name: 'about',
          path: '/about',
          meta: {
            isCenteredPage: true,
          },
          component: () => import('../pages/static/AboutUs/AboutUs.vue'),
        },
        {
          name: 'become-pro',
          path: '/become-pro',
          meta: {
          },
          component: () => import('../pages/static/becomePro/BecomePro.vue'),
        },
        {
          name: 'cashback',
          path: '/cashback',
          meta: {},
          component: () => import('../pages/static/Cashback/Cashback.vue'),
        },
        {
          name: 'legit',
          path: '/legit',
          meta: {
            shouldUseBg: ClientCatalogModel.BG.NEUTRAL,
            isCenteredPage: true,
          },
          component: () => import('../pages/static/Legit.vue'),
        },
        {
          name: '404',
          path: '*',
          meta: {
            shouldUseBg: ClientCatalogModel.BG.NEUTRAL,
          },
          component: () => import('../pages/static/Error404.vue'),
          beforeEnter(to, from, next) {
            setNotFoundState(store, context);
            next();
          },
        },
      ],
    },
  ];

  const router = new VueRouter({
    mode: 'history',
    routes,
  });

  router.beforeEach((to, from, next) => {
    let currentUser = store?.state?.user;
    if (typeof window !== 'undefined' && window.user) {
      currentUser = window.user;
    }

    if (!currentUser || currentUser.role === UserModel.ROLES.CUSTOMER.id) {
      if (to.name === 'game__product-detail' || to.name.startsWith('game__tag-as-product--')) {
        const productId = to.meta?.tagAsOfferInfo?.offerId || +extractIdFromSlug(to.params.id);
        criteoAnalytics.fireProductPageVisit(currentUser?.email, productId);
        getBlue.fireProductVisit(productId);
      } else if (to.matched.find((route) => ['root'].includes(route.name))) {
        criteoAnalytics.fireMainPageVisit(currentUser?.email);
        getBlue.firePageVisit();
      } else if (to.matched.find((route) => ['games__namespace', 'blog'].includes(route.name))) {
        if (to.matched[to.matched.length - 1].name !== 'game__service') {
          criteoAnalytics.firePageVisit(currentUser?.email);
        }
        getBlue.firePageVisit();
      }
    }

    for (let i = 0; i < to.matched.length; i++) {
      if (to.matched[i].meta?.authorizedOnly && !currentUser) {
        return next(getRedirectPath(to));
      }

      if (!isNavigationAllowed(to.matched[i], currentUser)) {
        return next('/');
      }
    }

    if (to.name.startsWith('game__')) {
      const game = GameModel.ID_TO_DATA[to.meta?.tagAsOfferInfo?.gameId]
        || GameModel.ROUTE_TO_DATA[to.params.namespace];

      if (!game || (!game?.active && (!to.query?.preview || !to.meta?.isPreviewAvailable))) {
        setNotFoundState(store, context);
        return next();
      }

      if (store.state.activeGame.id !== game.id) {
        store.commit('mutateActiveGame', game.id);
      }

      if (typeof window !== 'undefined' && 'localStorage' in window) {
        localStorage.setItem('activeGameId', game.id);
      }

      if (to.name === 'game__root') {
        store.commit('products/mutateEntity', {
          entity: 'headerInfo',
          data: {
            title: SeoModel.titleByGameId[game.id],
          },
        });
      }
    } else if (to.name.startsWith('blog__')) {
      const game = GameModel.ROUTE_TO_DATA[to.params.gameNamespace];

      if (to.params.gameNamespace && !game) {
        setNotFoundState(store, context);
        return next();
      }
    } else if (to.name.startsWith('client')) {
      return next();
    }

    // пока реализовано только в админке
    to.matched.forEach((m) => {
      if (context && m?.meta?.chunksToPrefetch?.length) {
        m.meta.chunksToPrefetch.forEach((chunk) => {
          const prefetchFiles = Object.entries(context.mixManifest.normal)
            .filter(([key]) => key.match(`/${chunk}.(css|js)`))
            .map(([, value]) => value);

          prefetchFiles.forEach((file) => {
            const type = file.slice(-3) === 'css' ? 'style' : 'script';
            context.meta += `<link rel="prefetch" href="${file}" as="${type}">\n`;
          });
        });
      }
    });

    return next();
  });

  router.afterEach((to, from) => {
    to.matched.some((m) => {
      if (context && m?.meta?.shouldUseBg) {
        let directoryName;

        if (m.meta.shouldUseBg === ClientCatalogModel.BG.CUSTOM && m.meta.shouldUseBgDirectory) {
          directoryName = m.meta.shouldUseBgDirectory;
        } else {
          directoryName = m.meta.shouldUseBg === ClientCatalogModel.BG.NEUTRAL
            ? 'neutral'
            : store.state.activeGame.id;
        }

        const formatToLoad = context.supportsImageFormat === 'avif' ? context.supportsImageFormat : 'jpg';

        /* eslint-disable max-len */
        context.meta += `<link rel="preload" href="/images/games/backgrounds/${directoryName}/bg-375.${formatToLoad}" as="image" type="image/${formatToLoad}" media="(-webkit-max-device-pixel-ratio:1.999) and (max-width:375px)">`;
        context.meta += `<link rel="preload" href="/images/games/backgrounds/${directoryName}/bg-750.${formatToLoad}" as="image" type="image/${formatToLoad}" media="(-webkit-min-device-pixel-ratio:2) and (max-width:375px),(-webkit-max-device-pixel-ratio:1.999) and (min-width: 376px) and (max-width:750px)">`;
        context.meta += `<link rel="preload" href="/images/games/backgrounds/${directoryName}/bg-1100.${formatToLoad}" as="image" type="image/${formatToLoad}" media="(-webkit-min-device-pixel-ratio:2) and (min-width:376px) and (max-width: 750px),(-webkit-max-device-pixel-ratio:1.999) and (min-width:751px) and (max-width:1100px)">`;
        context.meta += `<link rel="preload" href="/images/games/backgrounds/${directoryName}/bg-1400.${formatToLoad}" as="image" type="image/${formatToLoad}" media="(min-width:1101px) and (max-width:1400px)">`;
        context.meta += `<link rel="preload" href="/images/games/backgrounds/${directoryName}/bg-1920.${formatToLoad}" as="image" type="image/${formatToLoad}" media="(min-width:1401px) and (max-width:1920px)">`;
        context.meta += `<link rel="preload" href="/images/games/backgrounds/${directoryName}/bg-2800.${formatToLoad}" as="image" type="image/${formatToLoad}" media="(min-width:1921px)">`;
        /* eslint-enable max-len */

        return true;
      }

      return false;
    });

    if (to.meta?.shouldUseBg) {
      store.dispatch('setDefaultBackground', {
        isNeutral: to.meta?.shouldUseBg === ClientCatalogModel.BG.NEUTRAL,
        ...(to.meta?.shouldUseBg === ClientCatalogModel.BG.CUSTOM ? { custom: to.meta.shouldUseBgDirectory } : {}),
      });
    } else if (to.meta?.shouldUseBg !== ClientCatalogModel.BG.SKIP) {
      store.commit('mutateBackgrounds', { backgrounds: [] });
    }

    // do not scroll if route remains the same (ex. changed only route param: /wow/pve -> /wow/pvp)
    if (typeof window !== 'undefined' && from.name !== null && from.path !== to.path) {
      window.scrollTo(0, 0);
    }

    if (typeof window !== 'undefined') {
      const params = saveAdvertisingParamsToSession();
      executeAfterLoad(() => {
        if (from.name !== null) {
          updateCanonical(to);
        }

        const currentUser = store?.state?.user;
        if (!UserModel.ROLES.MANAGERS_ROLES_LIST.some((role) => role.id === currentUser?.role)) {
          IntercomApiDriver.update({ last_request_at: Math.round((new Date()).getTime() / 1000) });
        }

        // отправляем последние utm-метки в Intercom
        if (Object.keys(params).length > 0) {
          IntercomApiDriver.update(params);
        }
        googleTagAnalytics.emitPageViewEvent(
          to.meta && to.meta.title ? to.meta.title : document.title,
          to.fullPath,
          document.location.host,
        );
        // update page for Intercom events
        if (window.ga) {
          window.ga(() => {
            const allGA = window.ga.getAll();
            if (allGA.length) {
              const name = allGA[0]?.model?.data?.ia?.[':name'];
              if (name) {
                window.ga(`${name}.set`, 'page', to.fullPath);
              }
            }
          });
        }
      });
    }
  });

  return router;
}
